import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMedia } from "react-media";
import { useParams, Link } from "react-router-dom";
import { Dimmer, Loader, Header, Segment, Grid, Divider, Icon, Button, Form } from "semantic-ui-react";
import { InspectionSection } from "./InspectionSection";
import { PublicWrapper } from "../../../../components/PublicWrapper";
import "./PublicInspections.scss";
import { DeepMap, FieldError, FormProvider, SubmitHandler, useForm, useFormContext } from "react-hook-form";
import {
  ControlledInput,
  ControlledTextArea,
  IStrictControlledInputProps,
  IStrictControlledTextAreaProps,
} from "../../../../components/ControlledForm";
import { ControlledPhoneNumber } from "../../../../components/global_components/form_components/ControlledPhoneNumber";
import { useToasts } from "react-toast-notifications";
import InspectionRequestModal from "./inspection-request-modal/InspectionRequestModal";
import { InspectionStatuses } from "./citizen-inspection-list/constants/InspectionStatus";
import useCountries from "../../../../hooks/useCountries";
import { FullAddress } from "../../../../utils/FullAddress";
import InspectionPhoneLink from "./InspectionPanel/components/InspectionPhoneLink";
import useInspectionsPublicOnSiteContactMutation from "../../hooks/inspections/public/usePublicInspectionOnSiteContactInfoMutation";
import usePublicInspection from "../../hooks/inspections/public/usePublicInspection";
import PublicInspectionSummary from "./InspectionSummary/PublicInspectionSummary";
import PublicReInspectionNeedsSchedulingMessage from "./PublicInspectionMessage/PublicReInspectionNeedsSchedulingMessage";
import PublicInspectionNeedsSchedulingMessage from "./PublicInspectionMessage/PublicInspectionNeedsSchedulingMessage";
import InspectionScheduledDateDisplay from "./InspectionScheduledDateDisplay";
import PublicInspectionPanelCompletedSegment from "./InspectionPanel/components/public/PublicInspectionPanelCompletedSegment";
import usePermit from "../../hooks/permits/usePermit";
import usePermitType from "../../hooks/permits/usePermitType";
import { useDocumentTitle } from "../../hooks/useDocumentTitle.jsx";
import { ToastStatuses } from "../../../../components/global_components/Toast";

export const PublicInspections = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const { data: inspectionData } = usePublicInspection({ id: id });
  const applicationId = useMemo(() => _.get(inspectionData, "data.attributes.record-id"), [inspectionData]);
  const permitQuery = usePermit({ permitId: applicationId });
  const permitsTypeId = useMemo(() => _.get(permitQuery.data, "data.attributes.permits-type-id"), [permitQuery.data]);
  const permitsTypeQuery = usePermitType({ permitTypeId: permitsTypeId });
  const { agencyCountry } = useCountries();

  const inspectionStatus = useMemo(() => inspectionData?.data.attributes.status, [inspectionData]);
  const assigneeFirst = useMemo(
    () => _.get(inspectionData, "data.attributes.assignee.data.attributes.first-name", ""),
    [inspectionData],
  );
  const assigneeLast = useMemo(
    () => _.get(inspectionData, "data.attributes.assignee.data.attributes.last-name", ""),
    [inspectionData],
  );
  const assignee = `${assigneeFirst} ${assigneeLast}`;
  const inspectionTitle = useMemo(() => _.get(inspectionData, "data.attributes.title"), [inspectionData]);
  const checklist = useMemo(() => _.get(inspectionData, "data.attributes.sections"), [inspectionData]);
  const status = useMemo(() => _.get(inspectionData, "data.attributes.status"), [inspectionData]);
  const permitsTypeName = useMemo(() => _.get(permitsTypeQuery.data, "data.attributes.name"), [permitsTypeQuery.data]);

  const addressData = inspectionData?.data.attributes.address;
  const showFlag = !!agencyCountry && addressData?.countryId !== agencyCountry?.id; // Only display flag if country differs from agency country
  const address = addressData ? (
    <FullAddress
      address={addressData.address}
      address2={addressData.address2 ?? null}
      city={addressData.city}
      state={addressData.state}
      zip={addressData.zip}
      attn={addressData.attn}
      countryId={showFlag ? addressData?.countryId : null}
    />
  ) : null;
  const isLoading = permitQuery.isLoading || permitsTypeQuery.isLoading;
  const contentRef = useRef(null);
  const confirmationNumber = permitQuery.data?.data.attributes["confirmation-number"]?.match(/.{3}/g)?.join("-");
  const reservationNumber = permitQuery.data?.data.attributes["reservation-number"];

  const isSmallScreen = useMedia({ query: "(max-width: 767px)" });
  const isLargeScreen = useMedia({ query: "(min-width: 1000px)" });
  const completeInspection = status == "passed" || status == "failed";

  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const onEditInspectionRequest = () => {
    setIsRequestModalOpen(true);
  };

  const reservationOrConfirmationNumber = reservationNumber || confirmationNumber;

  useDocumentTitle(
    reservationOrConfirmationNumber && inspectionTitle ? `${reservationOrConfirmationNumber} | ${inspectionTitle}` : "",
  );

  return (
    <PublicWrapper homeUrl={`/${site}/public/permits`} contentRef={contentRef}>
      <div>
        <InspectionRequestModal
          inspectionId={id}
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
        />
        <Grid style={{ marginTop: 24 }}>
          <RequestInspectionMessage inspectionId={id} />
          <Grid.Row>
            <Grid.Column
              className='overview'
              style={{
                margin: isSmallScreen ? "16px 24px" : 0,
                height: "fit-content",
                maxWidth: isLargeScreen ? 360 : undefined,
              }}
              mobile={16}
              tablet={6}
              computer={5}
            >
              <Segment basic>
                <Header as='h1'>{inspectionTitle}</Header>
                <div>
                  <Link
                    to={`/${site}/public/permits/details/${applicationId}`}
                    className='link-text tw-text-[16px] tw-font-bold tw-text-blue'
                  >
                    {permitsTypeName} {reservationOrConfirmationNumber}
                  </Link>
                </div>
                <PublicInspectionPanelCompletedSegment id={id} />
                <div className='infoGroup'>
                  <label> Address </label>
                  <p className='infoItem'> {address ? address : "No address listed"} </p>
                  <label> Inspector </label>
                  <p className='infoItem'> {assignee || "No inspector listed"} </p>
                  <label> Date </label>
                  <PublicDateField inspectionId={id} onEditRequest={onEditInspectionRequest} />
                </div>
              </Segment>
              {inspectionStatus !== "unscheduled" && (
                <>
                  <Divider />
                  <Segment basic>
                    <OnSiteContactInfo id={id} />
                  </Segment>
                </>
              )}
            </Grid.Column>
            <Dimmer active={isLoading}>
              <Loader />
            </Dimmer>
            <Grid.Column mobile={16} tablet={10} computer={11} style={{ marginTop: 0, paddingTop: 0 }}>
              <Segment basic style={{ marginTop: 0, paddingTop: 0 }}>
                {completeInspection && <PublicInspectionSummary id={id} />}
                {checklist &&
                  checklist.map((section: any) => {
                    return (
                      <InspectionSection
                        key={section.data.id}
                        section={section.data.attributes}
                        inspectionStatus={status}
                        sectionId={section.data.id}
                        inspectionId={id}
                        user='citizen'
                      />
                    );
                  })}
              </Segment>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </PublicWrapper>
  );
};

interface IInspectionDateField {
  inspectionId: string | number;
  onEditRequest: () => void;
}
function PublicDateField({ inspectionId, onEditRequest }: IInspectionDateField) {
  const { data } = usePublicInspection({ id: inspectionId });

  const attributes = data?.data.attributes;

  const status = attributes?.status;
  const scheduledDateTime = attributes?.["scheduled-at"];
  const scheduledDate = attributes?.["scheduled-date"];
  const scheduledMeridiem = attributes?.["scheduled-meridiem"];

  const isRequested = status === InspectionStatuses.REQUESTED;
  if (isRequested) {
    const preferences = attributes?.request.preferences ?? [];
    let preferredDate = "";
    let preferredMeridiem = "";
    if (preferences.length !== 0) {
      const firstPreference = preferences[0];
      preferredDate = firstPreference.date ?? "";
      preferredMeridiem = firstPreference.meridiem ?? "";
    }

    return (
      <div>
        <p className='infoItem tw-mb-0 tw-mr-1'>
          Requested{" "}
          {new Date(preferredDate).toLocaleDateString(undefined, { month: "short", year: "numeric", day: "numeric" })}{" "}
          {preferredMeridiem}
        </p>
        <Button
          onClick={onEditRequest}
          className='tw-border-none tw-bg-transparent tw-px-0 tw-py-1 tw-text-blue focus:tw-ring-2 focus:tw-ring-blue focus:tw-ring-offset-2'
        >
          Change Request
        </Button>
      </div>
    );
  }

  return (
    <p className='infoItem'>
      <InspectionScheduledDateDisplay
        scheduledDate={scheduledDate}
        scheduledDateTime={scheduledDateTime}
        scheduledMeridiem={scheduledMeridiem}
      />
    </p>
  );
}

function RequestInspectionMessage({ inspectionId }: { inspectionId: string | number }) {
  const { data } = usePublicInspection({ id: inspectionId });

  const reInspection = data?.data.attributes.reInspection;
  const reInspectionId = reInspection?.id;
  const reInspectionStatus = reInspection?.status;

  const status = data?.data.attributes.status;
  const isReInspection = !!reInspectionId;

  if (isReInspection && status === InspectionStatuses.FAILED && reInspectionStatus === InspectionStatuses.UNSCHEDULED) {
    return (
      <Grid.Row>
        <PublicReInspectionNeedsSchedulingMessage reInspectionId={reInspectionId} />
      </Grid.Row>
    );
  }

  if (status === InspectionStatuses.UNSCHEDULED) {
    return (
      <Grid.Row>
        <PublicInspectionNeedsSchedulingMessage inspectionId={inspectionId} />
      </Grid.Row>
    );
  }

  return <></>;
}

interface IOnSiteContactInfoProps {
  id: string | number;
}
interface IOnSiteContactInfo {
  name: string;
  phone: string;
  instructions: string;
}
function OnSiteContactInfo({ id }: IOnSiteContactInfoProps) {
  const [editing, setEditing] = useState(false);

  const { data } = usePublicInspection({ id });
  const { addToast } = useToasts();
  const onError = () => {
    addToast("On-site Contact Info Update Failed!", { appearance: ToastStatuses.Error, autoDismiss: true });
  };
  const onSuccess = () => {
    addToast("On-site Contact Info Update Succeeded!", { appearance: ToastStatuses.Success, autoDismiss: true });
  };

  const { updateOnSiteContact } = useInspectionsPublicOnSiteContactMutation({ id: id, onSuccess, onError });

  const formContext = useForm<IOnSiteContactInfo>({
    defaultValues: {
      name: "",
      phone: "",
      instructions: "",
    },
  });

  useEffect(() => {
    const attributes = data?.data.attributes;
    const name = attributes?.onSiteContactName;
    const phone = attributes?.onSiteContactPhone;
    const instructions = attributes?.onSiteContactApplicantInstructions;
    formContext.reset({
      name: name ?? "",
      phone: phone ?? "",
      instructions: instructions ?? "",
    });
  }, [data]);

  const onSave: SubmitHandler<IOnSiteContactInfo> = (data) => {
    updateOnSiteContact({
      onSiteContactName: data.name,
      onSiteContactPhone: data.phone,
      onSiteContactApplicantInstructions: data.instructions,
    });
    setEditing(false);
  };

  return (
    <FormProvider {...formContext}>
      <OnSiteContactInfoHeader
        id={id}
        editing={editing}
        onSave={formContext.handleSubmit(onSave)}
        onEdit={() => {
          setEditing(true);
        }}
        onCancel={() => {
          setEditing(false);
        }}
      />
      {editing ? <OnSiteContactInfoEdit errors={formContext.errors} /> : <OnSiteContactInfoDisplay id={id} />}
    </FormProvider>
  );
}

interface IOnSiteContactInfoHeader {
  id: number | string;
  editing: boolean;
  onSave: () => void;
  onEdit: () => void;
  onCancel: () => void;
}
function OnSiteContactInfoHeader({ id, editing, onSave, onCancel, onEdit }: IOnSiteContactInfoHeader) {
  const { isFetching, isLoading } = usePublicInspection({ id });
  const loading = isLoading || isFetching;

  // State and useEffects for managing focus of the edit/save/cancel buttons
  const [cancelRef, setCancelRef] = useState<Button | null>(null);
  const [saveRef, setSaveRef] = useState<Button | null>(null);
  useEffect(() => {
    if (cancelRef && editing) {
      cancelRef.focus();
    }
  }, [cancelRef]);
  const [editRef, setEditRef] = useState<Button | null>();
  useEffect(() => {
    if (editRef && !editing) {
      editRef.focus();
    }
  }, [cancelRef, saveRef]);

  const onSaveClick = () => {
    onSave();
  };

  return (
    <div className='tw-flex tw-justify-between'>
      <h2 className='tw-font-serif tw-text-sm tw-font-medium'>ONSITE CONTACT INFORMATION</h2>

      {editing ? (
        <div className='tw-flex tw-py-1'>
          <Button
            className=''
            basic
            size='tiny'
            onClick={onCancel}
            ref={(button) => {
              setCancelRef(button);
            }}
          >
            Cancel
          </Button>
          <Button
            className='tw-mr-0 tw-grow-0'
            primary
            size='tiny'
            onClick={onSaveClick}
            ref={(button) => {
              setSaveRef(button);
            }}
          >
            {loading ? <Loader active /> : "Save"}
          </Button>
        </div>
      ) : (
        <Button
          className='tw-text-sm'
          size='mini'
          basic
          onClick={onEdit}
          ref={(button) => {
            setEditRef(button);
          }}
        >
          <Icon name='edit outline'></Icon>Edit
        </Button>
      )}
    </div>
  );
}

interface IOnSiteContactInfoDisplay {
  id: number | string;
}
function OnSiteContactInfoDisplay({ id }: IOnSiteContactInfoDisplay) {
  const { data, isLoading, isFetching } = usePublicInspection({ id });

  const loading = isLoading || isFetching;

  const attributes = data?.data.attributes;
  const name = attributes?.onSiteContactName;
  const phone = attributes?.onSiteContactPhone;
  const instructions = attributes?.onSiteContactApplicantInstructions;

  if (!name && !phone && !instructions) {
    return <></>;
  }

  return (
    <>
      {name && (
        <>
          <label className='tw-font-bold'>
            Name <Loader className='tw-ml-1' size='mini' active={loading} inline></Loader>
          </label>
          <p> {name}</p>
        </>
      )}
      {phone && (
        <>
          <label className='tw-font-bold'>
            Phone <Loader className='tw-ml-1' size='mini' active={loading} inline></Loader>
          </label>
          <p>
            <InspectionPhoneLink phone={phone} />
          </p>
        </>
      )}
      {instructions && (
        <>
          <label className='tw-font-bold'>
            Message for Inspector <Loader className='tw-ml-1' size='mini' active={loading} inline></Loader>
          </label>
          <p>{instructions}</p>
        </>
      )}
    </>
  );
}

function OnSiteContactInfoEdit({ errors }: { errors: DeepMap<IOnSiteContactInfo, FieldError> }) {
  const { control } = useFormContext<IOnSiteContactInfo>();

  return (
    <Form>
      <Form.Field required error={!!errors.name}>
        <ControlledInput<IStrictControlledInputProps<"name", string>>
          label='Name'
          control={control}
          name={"name"}
          autoComplete='name'
          errors={errors}
          rules={{
            validate: {
              _length: (value) => {
                if (typeof value !== "string") {
                  return false;
                }
                const MAX_LENGTH = 191;
                if (value.length > MAX_LENGTH) {
                  return `Name cannot be greater than ${MAX_LENGTH} characters`;
                }
                if (value.length <= 0) {
                  return `Name cannot be blank`;
                }
                return true;
              },
            },
          }}
        />
      </Form.Field>
      <Form.Field error={!!errors.phone} required>
        <ControlledPhoneNumber
          label='Phone'
          control={control}
          name={"phone"}
          errors={errors}
          required
          autoComplete='tel'
        />
      </Form.Field>
      <Form.Field error={!!errors.instructions}>
        <ControlledTextArea<IStrictControlledTextAreaProps>
          label='Message for Inspector'
          control={control}
          name={"instructions"}
          rows={2}
          errors={errors}
          rules={{
            validate: {
              _length: (value) => {
                if (typeof value !== "string") {
                  return false;
                }
                const MAX_LENGTH = 191;
                if (value.length > MAX_LENGTH) {
                  return `Instructions cannot be greater than ${MAX_LENGTH} characters`;
                }
                return true;
              },
            },
          }}
        />
      </Form.Field>
    </Form>
  );
}

export default PublicInspections;
